import { gql } from '@apollo/client';
import { TestResult } from '../../types/backflowTest';
import { AssignmentStatus, BillingStatus } from '../../types/assignment';

export const GET_INBOUND_ASSIGNMENT_IDS = gql`
  query GetAssignmentIds(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
    $includeAddress: Boolean = false
  ) {
    inboundAssignments(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      groupBy: ["id"]
    ) {
      id
      jobId
      job @include(if: $includeAddress) {
        id
        address {
          latitude
          longitude
        }
      }
    }
  }
`;

export const GET_INBOUND_ASSIGNMENTS_COMPANY = gql`
  query GetAssignments(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    inboundAssignments(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      id
      jobId
      status
      billingStatus
      created
      assigned
      completed
      submitted

      customerProfile {
        id
        name
        phones {
          id
          value
        }
      }

      child {
        id
        due
        vendorProfile {
          id
          name
        }
      }

      job {
        address {
          id
          street1
          street2
          city
          state
          zip
          zipExt
        }
        backflowTest {
          jobId
          dateTested
          initialResult
          finalResult
          backflowAssemblyEdited
          properlyInstalled
          testable

          backflowAssembly {
            id
            waterSupplierProfile {
              id
              name
            }
            model {
              id
              name
              backflowAssemblyMake {
                name
              }
            }
            serialNumber
            size
            type
            location
            waterService {
              id
              pin
            }
          }
        }
        attachments {
          id
        }
      }
    }
  }
`;

export const GET_INBOUND_ASSIGNMENTS_INDIVIDUAL = gql`
  query GetAssignments(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    inboundAssignments(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      id
      jobId
      status
      created
      due
      assigned
      completed
      submitted

      parent {
        customerProfile {
          id
          name
          phones {
            id
            value
          }
        }
      }

      job {
        address {
          id
          street1
          street2
          city
          state
          zip
          zipExt
        }
        backflowTest {
          jobId
          dateTested
          initialResult
          finalResult
          testingCompanySubmitted
          backflowAssemblyEdited
          properlyInstalled
          testable

          backflowAssembly {
            waterSupplierProfile {
              id
              name
            }
            model {
              name
              backflowAssemblyMake {
                name
              }
            }
            serialNumber
            size
            type
            location
          }
        }
        attachments {
          id
        }
      }
    }
  }
`;

export type JobItemCompany = {
  id: string;
  jobId: string;
  status?: AssignmentStatus;
  billingStatus?: BillingStatus;
  created: number;
  assigned?: number;
  completed?: number;
  submitted?: number;

  customerProfile: {
    id: string;
    name: string;
    phones: {
      id: string;
      value: string;
    }[];
  };

  child?: {
    due?: number;
    vendorProfile: {
      name: string;
    };
  };

  job: {
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      zipExt?: string;
    };
    backflowTest?: {
      jobId: string;
      dateTested: string;
      initialResult?: TestResult;
      finalResult?: TestResult;
      testingCompanyName: string;
      testingCompanySubmitted: number;
      backflowAssemblyEdited: boolean;
      properlyInstalled: boolean;
      testable: boolean;
      backflowAssembly?: {
        waterSupplierProfile: {
          id: string;
          name: string;
        };
        model: {
          name: string;
          backflowAssemblyMake: {
            name: string;
          };
        };
        serialNumber: string;
        size: string | number;
        type: string;
        location: string;
        waterService: {
          id: string;
          pin: string;
        };
      };
    };
    attachments?: {
      id: string;
    }[];
  };
};

export type JobItemIndividual = {
  id: string;
  jobId: string;
  status?: AssignmentStatus;
  due?: number;
  created: number;
  assigned?: number;
  completed?: number;
  submitted?: number;

  parent?: {
    customerProfile: {
      id: string;
      name: string;
      phones: {
        id: string;
        value: string;
      }[];
    };
  };

  job: {
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      zipExt?: string;
    };
    backflowTest?: {
      jobId: string;
      dateTested: string;
      initialResult?: TestResult;
      finalResult?: TestResult;
      testingCompanyName: string;
      testingCompanySubmitted: number;
      backflowAssemblyEdited: boolean;
      properlyInstalled: boolean;
      testable: boolean;
      backflowAssembly?: {
        waterSupplierProfile: {
          id: string;
          name: string;
        };
        model: {
          name: string;
          backflowAssemblyMake: {
            name: string;
          };
        };
        serialNumber: string;
        size: string | number;
        type: string;
        location: string;
      };
      attachments?: {
        id: string;
      }[];
    };
  };
};
